import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { ConfirmarBorrado } from './ConfirmarBorrado.jsx'

/**
 * Estructura y seguridad de una tabla: columnas, índices, disparadores y RLS.
 *
 * Nada de esto concede privilegios nuevos —el rol del proyecto ya es dueño de su base y todo
 * esto se puede escribir en el editor SQL— pero evita teclearlo, escapa los identificadores
 * siempre y deja el SQL ejecutado en la auditoría.
 */
export function Estructura({ slug, esquema, tabla, columnas }) {
  const qc = useQueryClient()
  const [pestana, setPestana] = useState('indices')
  const [aBorrar, setABorrar] = useState(null)   // { que, nombre, extra }

  const av = useQuery({
    queryKey: ['avanzado', slug, esquema, tabla],
    queryFn: () => api.tablaAvanzado(slug, esquema, tabla),
  })
  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ['avanzado', slug, esquema, tabla] })
    qc.invalidateQueries({ queryKey: ['table', slug, esquema, tabla] })
    qc.invalidateQueries({ queryKey: ['tables', slug] })
  }
  const accion = useMutation({ mutationFn: ({ que, body }) => api.ddl(slug, que, body), onSuccess: refrescar })
  const borrado = useMutation({
    mutationFn: ({ que, body }) => api.ddl(slug, que, body),
    onSuccess: () => { setABorrar(null); refrescar() },
  })

  if (!av.data) return null
  const { rls, politicas, indices, triggers, funcionesTrigger } = av.data

  const PESTANAS = [
    ['indices', `Índices (${indices.length})`],
    ['triggers', `Disparadores (${triggers.length})`],
    ['rls', `Seguridad${rls.activa ? ' · activa' : ''}`],
  ]

  return (
    <div className="card" style={{ marginTop: 14 }}>
      <div className="tabs" role="tablist">
        {PESTANAS.map(([id, txt]) => (
          <button key={id} role="tab" aria-selected={pestana === id}
                  className={`tab ${pestana === id ? 'activa' : ''}`}
                  onClick={() => setPestana(id)}>{txt}</button>
        ))}
      </div>

      <div className="card-pad">
        {accion.isError && (
          <div className="alert error" style={{ marginBottom: 12 }}>
            {accion.error.body?.detail ?? accion.error.message}
          </div>
        )}

        {pestana === 'indices' && (
          <Indices indices={indices} columnas={columnas} esquema={esquema} tabla={tabla}
                   accion={accion} onBorrar={setABorrar} />
        )}
        {pestana === 'triggers' && (
          <Triggers triggers={triggers} funciones={funcionesTrigger} esquema={esquema} tabla={tabla}
                    accion={accion} onBorrar={setABorrar} />
        )}
        {pestana === 'rls' && (
          <Seguridad rls={rls} politicas={politicas} esquema={esquema} tabla={tabla}
                     accion={accion} onBorrar={setABorrar} />
        )}
      </div>

      <ConfirmarBorrado
        abierto={!!aBorrar} nombre={aBorrar?.nombre ?? ''} titulo={aBorrar?.titulo ?? 'Borrar'}
        ocupado={borrado.isPending}
        onCancelar={() => setABorrar(null)}
        onConfirmar={() => borrado.mutate({ que: aBorrar.que, body: aBorrar.body })}
      >
        <p style={{ marginTop: 0 }}>{aBorrar?.aviso}</p>
        <p style={{ marginBottom: 0 }}>No se puede deshacer.</p>
      </ConfirmarBorrado>
    </div>
  )
}

function Indices({ indices, columnas, esquema, tabla, accion, onBorrar }) {
  const [nombre, setNombre] = useState('')
  const [cols, setCols] = useState([])
  const [unico, setUnico] = useState(false)

  return (
    <>
      <table className="t">
        <thead><tr><th>Nombre</th><th>Definición</th><th /></tr></thead>
        <tbody>
          {indices.map((i) => (
            <tr key={i.nombre}>
              <td className="mono">{i.nombre}</td>
              <td className="mono ellipsis" style={{ color: 'var(--fg-3)', fontSize: 12 }}>{i.definicion}</td>
              <td style={{ textAlign: 'right' }}>
                {/* El índice de la clave primaria no se ofrece borrar: se iría con la restricción. */}
                {!/_pkey$/.test(i.nombre) && (
                  <button className="btn sm danger" onClick={() => onBorrar({
                    que: 'index/drop', nombre: i.nombre, titulo: 'Borrar el índice',
                    body: { esquema, nombre: i.nombre },
                    aviso: `Se borra el índice ${i.nombre}. Las consultas que lo usaban pasarán a recorrer la tabla entera.`,
                  })}>Borrar</button>
                )}
              </td>
            </tr>
          ))}
          {indices.length === 0 && <tr><td colSpan={3} style={{ color: 'var(--fg-3)' }}>Sin índices.</td></tr>}
        </tbody>
      </table>

      <div className="field-label" style={{ marginTop: 16 }}>Nuevo índice</div>
      <div className="toolbar">
        <input className="input" placeholder="nombre_del_indice" value={nombre} spellCheck="false"
               style={{ width: 220, fontFamily: 'var(--mono)' }}
               onChange={(e) => setNombre(e.target.value.trim())} />
        <select className="input" multiple value={cols} style={{ minWidth: 180, height: 76 }}
                onChange={(e) => setCols([...e.target.selectedOptions].map((o) => o.value))}>
          {columnas.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: 'var(--fg-2)' }}>
          <input type="checkbox" checked={unico} onChange={(e) => setUnico(e.target.checked)} /> único
        </label>
        <button className="btn primary" disabled={!nombre || !cols.length || accion.isPending}
                onClick={() => accion.mutate({ que: 'index', body: { esquema, tabla, nombre, columnas: cols, unico } })}>
          Crear
        </button>
      </div>
      <div className="field-hint">Varias columnas con ⌘ o Ctrl. El orden es el del listado.</div>
    </>
  )
}

function Triggers({ triggers, funciones, esquema, tabla, accion, onBorrar }) {
  const [nombre, setNombre] = useState('')
  const [cuando, setCuando] = useState('BEFORE')
  const [eventos, setEventos] = useState(['UPDATE'])
  const [funcion, setFuncion] = useState('')

  return (
    <>
      <table className="t">
        <thead><tr><th>Nombre</th><th>Definición</th><th /></tr></thead>
        <tbody>
          {triggers.map((t) => (
            <tr key={t.nombre}>
              <td className="mono">{t.nombre}</td>
              <td className="mono ellipsis" style={{ color: 'var(--fg-3)', fontSize: 12 }}>{t.definicion}</td>
              <td style={{ textAlign: 'right' }}>
                <button className="btn sm danger" onClick={() => onBorrar({
                  que: 'trigger/drop', nombre: t.nombre, titulo: 'Borrar el disparador',
                  body: { esquema, tabla, nombre: t.nombre },
                  aviso: `Se borra el disparador ${t.nombre} de ${esquema}.${tabla}. La función de Postgres que ejecutaba se queda.`,
                })}>Borrar</button>
              </td>
            </tr>
          ))}
          {triggers.length === 0 && <tr><td colSpan={3} style={{ color: 'var(--fg-3)' }}>Sin disparadores.</td></tr>}
        </tbody>
      </table>

      <div className="field-label" style={{ marginTop: 16 }}>Nuevo disparador</div>
      {funciones.length === 0 ? (
        <div className="alert warn">
          No hay ninguna función de Postgres que devuelva <span className="mono">trigger</span> en el
          esquema <span className="mono">{esquema}</span>. Créala primero en el editor SQL: la consola
          no genera PL/pgSQL, porque escribir el cuerpo de una función es escribir código.
        </div>
      ) : (
        <>
          <div className="toolbar">
            <input className="input" placeholder="nombre_del_trigger" value={nombre} spellCheck="false"
                   style={{ width: 200, fontFamily: 'var(--mono)' }}
                   onChange={(e) => setNombre(e.target.value.trim())} />
            <select className="input" value={cuando} onChange={(e) => setCuando(e.target.value)}>
              <option>BEFORE</option><option>AFTER</option>
            </select>
            <select className="input" multiple value={eventos} style={{ height: 76 }}
                    onChange={(e) => setEventos([...e.target.selectedOptions].map((o) => o.value))}>
              {['INSERT', 'UPDATE', 'DELETE', 'TRUNCATE'].map((x) => <option key={x}>{x}</option>)}
            </select>
            <select className="input" value={funcion} onChange={(e) => setFuncion(e.target.value)}>
              <option value="">función…</option>
              {funciones.map((f) => <option key={f} value={f}>{f}()</option>)}
            </select>
            <button className="btn primary" disabled={!nombre || !funcion || !eventos.length || accion.isPending}
                    onClick={() => accion.mutate({ que: 'trigger', body: { esquema, tabla, nombre, cuando, eventos, funcion } })}>
              Crear
            </button>
          </div>
          <div className="field-hint">Se crea <span className="mono">FOR EACH ROW</span>.</div>
        </>
      )}
    </>
  )
}

function Seguridad({ rls, politicas, esquema, tabla, accion, onBorrar }) {
  const [nombre, setNombre] = useState('')
  const [comando, setComando] = useState('ALL')
  const [rol, setRol] = useState('')
  const [using, setUsing] = useState('')
  const [check, setCheck] = useState('')
  const [editando, setEditando] = useState(null) // nombre de la política en edición

  return (
    <>
      <div className="toolbar">
        <span className={`badge ${rls.activa ? (rls.forzada ? 'done' : 'running') : ''}`}>
          <span className="dot" />
          {rls.activa ? (rls.forzada ? 'RLS activa y forzada' : 'RLS activa, NO forzada') : 'RLS desactivada'}
        </span>
        {!rls.activa && (
          <button className="btn sm primary" disabled={accion.isPending}
                  onClick={() => accion.mutate({ que: 'rls', body: { esquema, tabla, activar: true, forzar: true } })}>
            Activar y forzar
          </button>
        )}
        {rls.activa && !rls.forzada && (
          <button className="btn sm primary" disabled={accion.isPending}
                  onClick={() => accion.mutate({ que: 'rls', body: { esquema, tabla, activar: true, forzar: true } })}>
            Forzar
          </button>
        )}
        {rls.activa && (
          <button className="btn sm secondary" disabled={accion.isPending}
                  onClick={() => accion.mutate({ que: 'rls', body: { esquema, tabla, activar: false } })}>
            Desactivar
          </button>
        )}
      </div>

      {rls.activa && !rls.forzada && (
        <div className="alert warn" style={{ marginTop: 12 }}>
          <strong>Activa pero sin efecto.</strong> El dueño de la tabla se salta las políticas, y en
          esta plataforma <strong>la aplicación se conecta con el rol dueño</strong>. Sin{' '}
          <span className="mono">FORCE</span>, RLS aquí no protege nada.
        </div>
      )}
      {rls.activa && rls.forzada && politicas.length === 0 && (
        <div className="alert error" style={{ marginTop: 12 }}>
          <strong>Forzada y sin políticas: la tabla no devuelve ninguna fila a nadie.</strong> Añade
          una política antes de que la aplicación intente leerla.
        </div>
      )}

      <table className="t" style={{ marginTop: 14 }}>
        <thead><tr><th>Política</th><th>Para</th><th>Roles</th><th>Condición</th><th /></tr></thead>
        <tbody>
          {politicas.map((p) => (
            <tr key={p.nombre}>
              <td className="mono">{p.nombre}</td>
              <td>{p.comando}</td>
              <td className="mono" style={{ color: 'var(--fg-2)', fontSize: 12 }}>
                {/* name[] sin cast llegaba como texto «{a,b}»: nunca más un crash por esto */}
                {Array.isArray(p.roles) ? p.roles.join(', ') : String(p.roles ?? '').replace(/[{}"]/g, '')}
              </td>
              <td className="mono ellipsis" style={{ color: 'var(--fg-3)', fontSize: 12 }}>
                {p.using_ ? `USING ${p.using_}` : ''}{p.check_ ? ` CHECK ${p.check_}` : ''}
              </td>
              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button className="btn sm secondary" style={{ marginRight: 6 }}
                        onClick={() => {
                          setEditando(p.nombre)
                          setNombre(p.nombre)
                          setComando(p.comando ?? 'ALL')
                          setRol((p.roles?.[0] ?? '') === 'public' ? '' : (p.roles?.[0] ?? ''))
                          setUsing(p.using_ ?? '')
                          setCheck(p.check_ ?? '')
                        }}>
                  Editar
                </button>
                <button className="btn sm danger" onClick={() => onBorrar({
                  que: 'policy/drop', nombre: p.nombre, titulo: 'Borrar la política',
                  body: { esquema, tabla, nombre: p.nombre },
                  aviso: `Se borra ${p.nombre}. Si es la única y la RLS está forzada, la tabla dejará de devolver filas.`,
                })}>Borrar</button>
              </td>
            </tr>
          ))}
          {politicas.length === 0 && <tr><td colSpan={5} style={{ color: 'var(--fg-3)' }}>Sin políticas.</td></tr>}
        </tbody>
      </table>

      <div className="field-label" style={{ marginTop: 16 }}>
        {editando ? `Editar ${editando}` : 'Nueva política'}
      </div>
      {editando && (
        <div className="field-hint" style={{ marginBottom: 8 }}>
          Postgres no permite cambiar el <strong>comando</strong> de una política ni{' '}
          <strong>quitar</strong> una condición existente con ALTER: para eso, bórrala y créala de
          nuevo. Un campo vacío se queda como está.
        </div>
      )}
      <div className="toolbar">
        <input className="input" placeholder="nombre" value={nombre} spellCheck="false"
               disabled={!!editando}
               style={{ width: 170, fontFamily: 'var(--mono)' }}
               onChange={(e) => setNombre(e.target.value.trim())} />
        <select className="input" value={comando} disabled={!!editando}
                title={editando ? 'El comando no se puede alterar: borra y recrea la política' : undefined}
                onChange={(e) => setComando(e.target.value)}>
          {['ALL', 'SELECT', 'INSERT', 'UPDATE', 'DELETE'].map((x) => <option key={x}>{x}</option>)}
        </select>
        <input className="input" placeholder="rol (vacío = PUBLIC)" value={rol} spellCheck="false"
               style={{ width: 170, fontFamily: 'var(--mono)' }}
               onChange={(e) => setRol(e.target.value.trim())} />
      </div>
      <div className="toolbar" style={{ marginTop: 8 }}>
        <input className="input" placeholder="USING: qué filas se ven — p. ej. usuario_id = current_setting('app.usuario')::int"
               value={using} spellCheck="false" style={{ flex: 1, fontFamily: 'var(--mono)' }}
               onChange={(e) => setUsing(e.target.value)} />
      </div>
      <div className="toolbar" style={{ marginTop: 8 }}>
        <input className="input" placeholder="WITH CHECK: qué filas se pueden escribir (opcional)"
               value={check} spellCheck="false" style={{ flex: 1, fontFamily: 'var(--mono)' }}
               onChange={(e) => setCheck(e.target.value)} />
        {editando ? (
          <>
            <button className="btn primary" disabled={(!using && !check && !rol) || accion.isPending}
                    onClick={() => accion.mutate({
                      que: 'policy/alter',
                      body: { esquema, tabla, nombre: editando, rol: rol || 'PUBLIC', using, check },
                    }, {
                      onSuccess: () => { setEditando(null); setNombre(''); setUsing(''); setCheck(''); setRol('') },
                    })}>
              {accion.isPending ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button className="btn secondary" disabled={accion.isPending}
                    onClick={() => { setEditando(null); setNombre(''); setUsing(''); setCheck(''); setRol('') }}>
              Cancelar
            </button>
          </>
        ) : (
          <button className="btn primary" disabled={!nombre || (!using && !check) || accion.isPending}
                  onClick={() => accion.mutate({ que: 'policy', body: { esquema, tabla, nombre, comando, rol: rol || null, using, check } })}>
            Crear
          </button>
        )}
      </div>
      <div className="field-hint">
        La condición es una expresión SQL booleana evaluada por fila. Se puede pasar contexto a la
        sesión con <span className="mono">SET app.usuario = …</span> desde la aplicación.
      </div>
    </>
  )
}
